/**
 * Reactive net for "hashed lazy chunk 404s after a deploy" — icons and other
 * dynamically-imported modules (lazy routes, mermaid, …) whose old content-hashed
 * chunks were purged when a new build deployed.
 *
 * The primary defence is `registerType: 'autoUpdate'` (see registerServiceWorker.ts):
 * a freshly-deployed SW self-activates and serves the new shell, so stale shells
 * stop being served. This handler covers the short transition window before that
 * activation lands — e.g. a chunk that 404s mid-session. It activates a waiting SW
 * via `SKIP_WAITING` if one is already installed, then reloads so the tab comes back
 * on a shell whose chunk hashes exist. Guarded so it fires at most once per short
 * window and can't spin in a reload loop.
 */

import { activateNewestServiceWorker } from './activateNewestServiceWorker'

const RECOVERY_GUARD_KEY = 'evergpt-chunk-recovery-at'
const RECOVERY_GUARD_MS = 30_000

let recovering = false
/** Intended destination for an in-flight recovery, set by whichever caller knows it. */
let pendingTargetUrl: string | undefined

function recoveredRecently(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RECOVERY_GUARD_KEY) || 0)
    return Date.now() - last < RECOVERY_GUARD_MS
  } catch {
    return false
  }
}

function markRecovered(): void {
  try {
    sessionStorage.setItem(RECOVERY_GUARD_KEY, String(Date.now()))
  } catch {
    /* private mode / quota — best-effort guard only */
  }
}

/**
 * Activate the newest service worker (if one is waiting) then navigate, so the
 * page comes back on a shell whose chunk hashes actually exist on the server.
 * No-ops if we already attempted recovery inside the guard window.
 *
 * @param targetUrl Where to land after recovery. Defaults to reloading the
 *   current URL; the router passes the intended destination so an aborted
 *   navigation isn't silently dropped.
 */
export async function recoverFromChunkError(targetUrl?: string): Promise<void> {
  // A failed lazy-ROUTE chunk triggers the global `vite:preloadError` handler
  // (no target) slightly BEFORE `router.onError` (which knows the destination).
  // Remember the latest known target so the recovery already in flight lands on
  // the intended route instead of reloading the current one.
  if (targetUrl) pendingTargetUrl = targetUrl

  if (recovering || recoveredRecently()) return
  recovering = true
  markRecovered()

  // Hand control to the newest worker first, or the navigation below is answered
  // from the same stale precache it is trying to escape.
  await activateNewestServiceWorker()

  // Read at navigation time: a `router.onError` call may have supplied the
  // intended route while we were awaiting the service worker above.
  const destination = targetUrl ?? pendingTargetUrl
  if (destination) {
    window.location.href = destination
  } else {
    window.location.reload()
  }
}

/**
 * Global net for lazy-chunk load failures. Vite fires `vite:preloadError` whenever
 * a dynamically-imported chunk fails to load — this covers icons (both
 * `@foundever/ui-lib`'s `UIIcon` and the local one), lazy routes, mermaid, etc.
 * Register once, as early as possible.
 */
export function registerChunkErrorRecovery(): void {
  window.addEventListener('vite:preloadError', ((event: Event) => {
    // Stop Vite from re-throwing the preload error; we own recovery.
    event.preventDefault()
    void recoverFromChunkError()
  }) as EventListener)
}
